import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { IoReload } from "react-icons/io5";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { translations, formatDateTimeEsCO } from "../../../helpers";
import {
  BsPencil,
  BsEye,
  BsFillPlusCircleFill,
  BsArrowDown,
  BsArrowUp,
  BsArrowDownUp,
  BsSearch,
  BsInfoCircle,
} from "../../../assets/icons/vander";
import { usePatientsStore, useForm } from "../../../hooks";
import {
  SelectForm,
  LoadingData,
  ErrorMessage,
  SuccessMessage,
  InputForm,
} from "../../ui";
import { paginationOptions } from "../../data";
import { ModalViewPatient, ModalEditPatient, ModalSendQuiz } from "../";

const formValidations = {};

const stateProps = {
  QUIZ_SEND: {
    badge: {
      className: "bg-gray-300",
      text: "Encuesta enviada",
    },
    tooltip: null,
  },
  QUIZ_ANSWERED: {
    badge: {
      className: "bg-green text-white",
      text: "Encuesta completada",
    },
    tooltip: null,
  },
  SCHEDULED_ASSESSMENT: {
    badge: {
      className: "bg-gray-300",
      text: "Consulta agendada",
    },
    tooltip: null,
  },
  RESCHEDULED_ASSESSMENT: {
    badge: {
      className: "bg-gray-300",
      text: "Consulta agendada",
    },
    tooltip: null,
  },
  ASSESSMENT_NO_SHOW: {
    badge: {
      className: "bg-pink",
      text: "No asistió",
    },
    tooltip: null,
  },
  ASSESSMENT_MADE: {
    YES: {
      badge: {
        className: "bg-green text-white",
        text: "Avalado",
      },
      tooltip: null,
    },
    DEFAULT: {
      badge: {
        className: "bg-green text-white",
        text: "Valoración realizada",
      },
      tooltip: null,
    },
  },
  ASSESSMENT_MADE_RESCHEDULE: {
    NO_BUT_CAN_RESCHEDULE: {
      badge: {
        className: "bg-orange text-white",
        text: "Reagendable",
      },
      tooltip: {
        color: "text-orange",
        text: ["clearanceNote"],
      },
    },
    DEFAULT: {
      badge: {
        className: "bg-orange text-white",
        text: "Reagendado",
      },
      tooltip: {
        color: "text-orange",
      },
    },
  },
  SCHEDULED_ASSESSMENT_FINAL: {
    badge: {
      className: "bg-gray-300",
      text: "Finalización agendada",
    },
    tooltip: null,
  },
  ASSESSMENT_NO_SHOW_FINAL: {
    badge: {
      className: "bg-orange text-white",
      text: "No asistió a finalización",
    },
    tooltip: null,
  },
  FINISHED: {
    NO: {
      badge: {
        className: "bg-red text-white",
        text: "No avalado",
      },
      tooltip: {
        color: "text-red",
        text: ["clearanceNote"],
      },
    },
    DEFAULT: {
      badge: {
        className: "bg-green text-white",
        text: "Finalizado",
      },
      tooltip: null,
    },
  },
  DEFAULT: {
    badge: {
      className: "bg-gray-300",
      text: "Registrado",
    },
    tooltip: null,
  },
};

const getStateProps = (patient) => {
  // Si no hay lastPatientSurgery, usar DEFAULT
  if (!patient.lastPatientSurgery) {
    return stateProps.DEFAULT;
  }

  const state = patient.lastPatientSurgery.state;
  const stateConfig = stateProps[state];
  const procedureEvents =
    patient.lastPatientSurgery.scheduleEvents?.filter(
      (event) => event.type === "PROCEDURE"
    ) || [];
  const numberEvents = patient.lastPatientSurgery.numberEvents || 0;
  const completedProcedures = procedureEvents.filter(
    (event) => event.attended === true
  ).length;

  if (stateConfig && stateConfig.DEFAULT) {
    const clearanceStatus = patient.lastPatientSurgery.clearanceStatus;
    let props = stateConfig[clearanceStatus] || stateConfig.DEFAULT;

    // Si es ASSESSMENT_MADE y hay procedimientos
    if (state === "ASSESSMENT_MADE") {
      if (completedProcedures === numberEvents && numberEvents > 0) {
        // Todos los procedimientos completados
        props = {
          badge: {
            className: "bg-green-700 text-white",
            text: "Cirugía completada",
          },
          tooltip: null,
        };
      } else {
        // Ordenar eventos por fecha (más reciente primero)
        const sortedEvents = procedureEvents.sort(
          (a, b) => new Date(b.start) - new Date(a.start)
        );
        const latestEvent = sortedEvents[0];

        if (latestEvent?.attended === null) {
          // El evento más reciente está agendado
          props = {
            badge: {
              className: "bg-gray-300",
              text: "Cirugía asignada",
            },
            tooltip: null,
          };
        } else if (latestEvent?.attended === false) {
          // El evento más reciente no fue asistido
          props = {
            badge: {
              className: "bg-pink text-white",
              text: "No asistió a cirugía",
            },
            tooltip: null,
          };
        }
      }
    }

    return props;
  }

  // Si lastPatientSurgery existe pero el estado no está en stateProps
  return (
    stateConfig || {
      badge: {
        className: "",
        text: "Desconocido",
      },
      tooltip: null,
    }
  );
};

const getTooltipText = (patient) => {
  const stateConfig = getStateProps(patient);
  const tooltipText = stateConfig.tooltip?.text;

  if (Array.isArray(tooltipText)) {
    return tooltipText
      .map((key) => patient.lastPatientSurgery?.[key] || "Sin observaciones")
      .join(", ");
  }

  return tooltipText || "Sin información";
};

const getFullTooltipText = (patient) => {
  return getTooltipText(patient);
};

export const PatientsTable = () => {
  // Datos del hook ----------------------------------------//
  const {
    // Props
    isLoadingData,
    patients,
    message,
    page,
    pageSize,
    totalPages,
    filters,
    sort,

    // Methods
    startGetPatients,
    startChangePageSize,
    startChangePage,
    startAddPage,
    startRemovePage,
    reloadPatients,
    startChangeSort,
    startSearch,
    startGetPatientByEncryptedId,
  } = usePatientsStore();

  const [searchParams] = useSearchParams();

  // States ------------------------------------------------//
  const [viewProfile, setViewProfile] = useState(false);
  const [editPatient, setEditPatient] = useState(false);
  const [isSendQuiz, setIsSendQuiz] = useState(false);
  const [patient, setPatient] = useState(null);
  const [formData, setFormData] = useState(filters);
  const [hasSearchedByKey, setHasSearchedByKey] = useState(false);

  // Formulario --------------------------------------------//
  const { formState, onInputChange } = useForm(formData, formValidations);

  // Funciones ---------------------------------------------//
  const onChangePageSize = ({ target }) => {
    startChangePageSize(target.value);
  };

  const onChangePage = (page) => {
    startChangePage(page);
  };

  const onSubmit = () => {
    startSearch(formState);
  };

  // Efectos -----------------------------------------------//
  useEffect(() => {
    const encryptedId = searchParams.get("key");

    if (encryptedId && !hasSearchedByKey) {
      setHasSearchedByKey(true);
      startGetPatientByEncryptedId(encryptedId).then((found) => {
        if (!found) {
          startGetPatients(page, pageSize, { filters, sort });
        }
      });
    } else if (!encryptedId && patients.length === 0) {
      startGetPatients(page, pageSize, { filters, sort });
    }
  }, [searchParams]);

  useEffect(() => {
    if (!searchParams.get("key") && hasSearchedByKey) {
      setHasSearchedByKey(false);
    }
  }, [searchParams, hasSearchedByKey]);

  useEffect(() => {
    if (!editPatient) {
      setPatient(null);
    }
  }, [editPatient]);

  // useEffect(() => {
  //   console.log("patients", patients);
  // }, [patients]);

  return (
    <>
      <div className="vstack gap-3">
        <div className="row justify-content-between">
          <div className="col-12 col-md-6 col-lg-4 col-xl-3">
            <div className="d-flex align-items-end">
              <SelectForm
                label={`Registros por página`}
                options={paginationOptions}
                labelKey={"description"}
                name={`pageSize`}
                value={pageSize}
                onInputChange={onChangePageSize}
              />
              <button
                className="btn btn-icon btn-pills btn-soft-primary ms-4"
                onClick={() => {
                  reloadPatients();
                }}
              >
                <IoReload />
              </button>
            </div>
          </div>
        </div>

        <div className="input-group">
          <input
            type={"text"}
            className={`form-control`}
            placeholder={"Buscar paciente"}
            name={"search"}
            value={formState.search}
            onChange={onInputChange}
          />
          <button className="btn btn-cyan" type="button" onClick={onSubmit}>
            <BsSearch />
          </button>
        </div>

        {isLoadingData && <LoadingData />}

        {message?.type === "errorPatients" && (
          <ErrorMessage>{message.text}</ErrorMessage>
        )}
        {message?.type === "success" && (
          <SuccessMessage>{message.text}</SuccessMessage>
        )}

        <div className="table-responsive shadow rounded">
          <table className="table table-center bg-white mb-0">
            <thead>
              <tr>
                <th className="p-3">#</th>
                <th
                  className="p-3"
                  onClick={() => startChangeSort("document")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span>Documento</span>
                    <div className="text-gray">
                      {sort.key === "document" ? (
                        sort.direction === "asc" ? (
                          <BsArrowDown />
                        ) : (
                          <BsArrowUp />
                        )
                      ) : (
                        <BsArrowDownUp />
                      )}
                    </div>
                  </div>
                </th>
                <th
                  className="p-3"
                  onClick={() => startChangeSort("firstName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span>Nombres</span>
                    <div className="text-gray">
                      {sort.key === "firstName" ? (
                        sort.direction === "asc" ? (
                          <BsArrowDown />
                        ) : (
                          <BsArrowUp />
                        )
                      ) : (
                        <BsArrowDownUp />
                      )}
                    </div>
                  </div>
                </th>
                <th
                  className="p-3"
                  onClick={() => startChangeSort("lastName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span>Apellidos</span>
                    <div className="text-gray">
                      {sort.key === "lastName" ? (
                        sort.direction === "asc" ? (
                          <BsArrowDown />
                        ) : (
                          <BsArrowUp />
                        )
                      ) : (
                        <BsArrowDownUp />
                      )}
                    </div>
                  </div>
                </th>
                <th className="p-3">Propósito</th>
                <th className="p-3">Estado</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="fs-sm">
              {patients.map((patient, index) => (
                <tr key={patient.id}>
                  <th className="p-3">{index + 1}</th>
                  <td className="py-3">{patient.document}</td>
                  <td className="py-3">{patient.firstName}</td>
                  <td className="py-3">{patient.lastName}</td>
                  <td className="py-3">
                    {patient.lastPatientSurgery?.procedure?.title ||
                      "Por asignar"}
                  </td>
                  <td className="py-3">
                    <div className="d-flex align-items-center">
                      <span
                        className={`flex-shrink-0 rounded-pill px-2 py-1 small text-uppercase ${
                          getStateProps(patient).badge.className
                        }`}
                      >
                        {getStateProps(patient).badge.text}
                      </span>
                      {getStateProps(patient).tooltip && (
                        <OverlayTrigger
                          placement="left"
                          overlay={
                            <Tooltip>{getFullTooltipText(patient)}</Tooltip>
                          }
                        >
                          <button
                            style={{ width: "24px" }}
                            className={`btn btn-icon fs-lg ${
                              getStateProps(patient).tooltip.color || ""
                            } ms-1`}
                          >
                            <BsInfoCircle className="icon" />
                          </button>
                        </OverlayTrigger>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="d-flex align-items-center justify-content-end">
                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Ver paciente</Tooltip>}
                      >
                        <button
                          style={{ width: "24px" }}
                          className="btn btn-icon fs-lg text-gray-400"
                          onClick={() => {
                            setPatient(patient);
                            setViewProfile(true);
                          }}
                        >
                          <BsEye />
                        </button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Editar paciente</Tooltip>}
                      >
                        <button
                          style={{ width: "24px" }}
                          className="btn btn-icon fs-lg text-gray-300"
                          onClick={() => {
                            setPatient(patient);
                            setEditPatient(true);
                          }}
                        >
                          <BsPencil />
                        </button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip>Enviar flujo</Tooltip>}
                      >
                        <button
                          style={{ width: "24px" }}
                          className="btn btn-icon fs-lg text-warning"
                          onClick={() => {
                            setPatient(patient);
                            setIsSendQuiz(true);
                          }}
                        >
                          <BsFillPlusCircleFill />
                        </button>
                      </OverlayTrigger>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ul className="pagination justify-content-end">
          <li className="page-item" onClick={() => startRemovePage()}>
            <Link className="page-link" to="#" aria-label="Previous">
              &lt;
            </Link>
          </li>
          {Array.from({ length: totalPages }, (_, index) => (
            <li
              key={index}
              className={`page-item ${
                parseInt(page) === index + 1 ? "active" : ""
              }`}
              onClick={() => onChangePage((index + 1).toString())}
            >
              <Link className={`page-link`} to="#">
                {index + 1}
              </Link>
            </li>
          ))}
          <li className="page-item" onClick={() => startAddPage()}>
            <Link className="page-link" to="#" aria-label="Next">
              &gt;
            </Link>
          </li>
        </ul>
      </div>

      <ModalViewPatient
        viewProfile={viewProfile}
        setViewProfile={setViewProfile}
        patient={patient}
      />

      <ModalEditPatient
        editPatient={editPatient}
        setEditPatient={setEditPatient}
        patient={patient}
      />

      <ModalSendQuiz
        isSendQuiz={isSendQuiz}
        setIsSendQuiz={setIsSendQuiz}
        patient={patient}
      />
    </>
  );
};
